import { ARTICLE_PROMPT_IDS } from './articlePilotRoute.js';

const PAIR_IDS = Object.freeze([ARTICLE_PROMPT_IDS.partTime, ARTICLE_PROMPT_IDS.neighbourhood]);
const hasAttempt = session => typeof session?.schoolAttemptId === 'string' && session.schoolAttemptId.trim().length > 0;
const isPair = (id, pairIds) => Array.isArray(pairIds) ? pairIds.includes(id) : pairIds instanceof Set && pairIds.has(id);

// A setup URL describes the next explicit Start. Once an attempt exists, its
// retained classification is the only authority for its displayed mode.
export function articleSessionIndependent(session, requested) {
    if (session == null) return requested === true;
    if (typeof session !== 'object' || Array.isArray(session) || !isPair(session.promptId, PAIR_IDS)) return false;
    if (session.phase === 'setup' || !hasAttempt(session)) return requested === true;
    return ['running', 'done'].includes(session.phase) && session.independent === true && session.supportUsed !== true;
}

// Classification belongs to one actual Start, identified by its existing attempt
// ID. A URL can request a new independent Start; it cannot reclassify an existing
// supported attempt or erase its support record. This is page memory only.
export function retainArticleSession(previous, snapshot, { requestedIndependent = false, pairIds = PAIR_IDS } = {}) {
    if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) return null;
    const sameAttempt = hasAttempt(snapshot) && hasAttempt(previous)
        && previous.schoolAttemptId === snapshot.schoolAttemptId
        && previous.packId === snapshot.packId && previous.promptId === snapshot.promptId;
    const supportUsed = snapshot.supportUsed === true || (sameAttempt && previous.supportUsed === true);
    const validPhase = snapshot.phase === 'running' || snapshot.phase === 'done';
    const independent = hasAttempt(snapshot) && validPhase && isPair(snapshot.promptId, pairIds) && !supportUsed
        && (sameAttempt ? previous.independent === true : snapshot.phase === 'running' && requestedIndependent === true);
    return { ...snapshot, independent, supportUsed };
}

// A guard uses the original deadline, rather than a stale retained countdown.
// Return the original record by identity; inspecting navigation never advances,
// starts or submits an attempt. Arrays, Map.values() and Maps are accepted.
export function findRunningIndependent(values, now = Date.now()) {
    if (!Number.isFinite(now)) return null;
    const rows = values instanceof Map ? values.values() : values;
    if (!rows || typeof rows === 'string' || typeof rows[Symbol.iterator] !== 'function') return null;
    for (const session of rows) {
        if (session?.phase === 'running' && session.independent === true && session.supportUsed !== true
            && hasAttempt(session) && isPair(session.promptId, PAIR_IDS)
            && Number.isFinite(session.deadlineAt) && session.deadlineAt > now) return session;
    }
    return null;
}

// Explicitly switching to supported practice preserves text, clock, existing
// attempt ID and pending school-save handle. It creates no additional attempt.
export function markArticleSessionSupported(session) {
    return session && typeof session === 'object' && !Array.isArray(session)
        ? { ...session, independent: false, supportUsed: true } : null;
}
